import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Platform, Pressable, ScrollView, TextInput, View, Text, StyleSheet } from 'react-native';
import { StatsAggReportService } from '../../../services/stats';
import { AccountPracInfoService } from '../../../services/account/AccountPracInfoService';
import { AccountPracInfoStorage } from '../../../storage/AccountPracInfoStorage';
import { AdminAssessmentStorage } from '../../../services/AdminAssessmentStorage';
import type { AdminAssessmentDto } from '../../../types/adminAssessment';
import type {
  AccountPlaDto,
  AccountPracInfoResponseDto,
  StatsAggFilterDefDto,
  StatsAggReportDefDto,
  StatsAggReportItemDefDto,
} from '../../../types/api';
import { EDITOR_COLORS, editorText, webControlStyle } from '../../../styles/editorTheme';
import { OutlineCheckboxBox, renderWebOutlineCheckbox } from '../../OutlineCheckbox';
import { resolveAuthContext } from '../../../services/auth/authContext';
import { AGG_FILTER_CATALOG } from './aggReportConstants';
import { AggReportItemListEditor } from './AggReportItemListEditor';

interface AggReportSetupViewProps {
  storage?: any;
  apiService?: any;
  accountId?: string | null;
  /** Which report to open editing on, or '' to start a blank/new draft. */
  initialReportId?: string | null;
  onBack: () => void;
  /** Called after a successful save with the persisted (possibly newly-assigned) ReportId, so
   * the parent can preselect it when the user later returns to the report view. */
  onSaved: (reportId: string) => void;
}

const resolveUserStore = (): any => resolveAuthContext().userStore || null;

const parsePos = (pos: string): number => {
  const n = parseInt(pos, 10);
  return Number.isNaN(n) ? Number.MAX_SAFE_INTEGER : n;
};

const byPos = (a: { Pos: string }, b: { Pos: string }): number => parsePos(a.Pos) - parsePos(b.Pos);

/** Drop any filter whose Name is no longer in the catalog (e.g. a report saved with the old
 * 'period'/'previous' filters) and put the rest in Pos order. */
const normalizeFilters = (filters: StatsAggFilterDefDto[]): StatsAggFilterDefDto[] =>
  filters.filter((f) => AGG_FILTER_CATALOG.some((e) => e.name === f.Name)).sort(byPos);

interface AggAssessmentOption {
  value: string;
  label: string;
  aggVars: Array<{ value: string; label: string }>;
}

export const AggReportSetupView: React.FC<AggReportSetupViewProps> = ({
  storage = null,
  apiService = null,
  accountId = null,
  initialReportId = null,
  onBack,
  onSaved,
}) => {
  const [reportDefs, setReportDefs] = useState<StatsAggReportDefDto[]>([]);
  const [assessments, setAssessments] = useState<AdminAssessmentDto[]>([]);
  const [plaList, setPlaList] = useState<AccountPlaDto[]>([]);
  const [selectedReportId, setSelectedReportId] = useState<string>('');
  const [draftDName, setDraftDName] = useState('');
  const [draftFilters, setDraftFilters] = useState<StatsAggFilterDefDto[]>([]);
  const [draftItems, setDraftItems] = useState<StatsAggReportItemDefDto[]>([]);
  const [isSavingRemote, setIsSavingRemote] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const hasAppliedInitialReportId = useRef(false);

  const aggReportService = useMemo(() => (apiService ? new StatsAggReportService(apiService) : null), [apiService]);
  const accountPracInfoService = useMemo(() => (apiService ? new AccountPracInfoService(apiService) : null), [apiService]);
  const adminAssessmentStorage = useMemo(() => (storage ? new AdminAssessmentStorage(storage) : null), [storage]);

  // Tracks which report the draft fields currently reflect, so the hydration effect below can
  // depend on reportDefs directly (needed so a not-yet-loaded initialReportId still hydrates
  // once its def arrives) without a background refresh re-stomping in-progress edits to that
  // same report every time reportDefs' identity changes.
  const hydratedForReportId = useRef<string | null>(null);

  useEffect(() => {
    if (!hasAppliedInitialReportId.current && initialReportId) {
      hasAppliedInitialReportId.current = true;
      setSelectedReportId(initialReportId);
    }
  }, [initialReportId]);

  // Load saved report definitions + the account's enabled PLA list — cache-first, then a
  // background refresh, same pattern as AggReportView/UsageView.
  useEffect(() => {
    let cancelled = false;

    const applyAccountData = (data: AccountPracInfoResponseDto | null | undefined) => {
      if (!data || cancelled || !data.AccountSettings) {
        return;
      }
      setReportDefs((data.AccountSettings.AggReportDefs ?? []).filter((def) => def.Status !== '-1'));
      setPlaList(data.AccountSettings.PlaList ?? []);
    };

    const loadAccountData = async () => {
      if (!accountId) {
        return;
      }
      if (storage) {
        try {
          const pracStorage = new AccountPracInfoStorage(storage);
          applyAccountData(await pracStorage.getAccountPracInfo(accountId));
        } catch (cacheError) {
          console.warn('AggReportSetupView: failed to read cached account data', cacheError);
        }
      }
      if (!accountPracInfoService) {
        return;
      }
      try {
        const dto = await accountPracInfoService.getAccountPracInfo(
          { AccountId: accountId, Types: ['AccountSettings'] },
          { userStore: resolveUserStore(), storage }
        );
        if (cancelled) {
          return;
        }
        applyAccountData(dto);
        if (storage) {
          const pracStorage = new AccountPracInfoStorage(storage);
          const latestCached = await pracStorage.getAccountPracInfo(accountId);
          const merged = latestCached ? { ...latestCached, ...dto } : dto;
          await pracStorage.saveAccountPracInfo(accountId, merged);
        }
      } catch (fetchError) {
        console.warn('AggReportSetupView: failed to refresh account data', fetchError);
      }
    };

    void loadAccountData();

    return () => {
      cancelled = true;
    };
  }, [accountId, accountPracInfoService, storage]);

  // Assessment content (aggVars/dName) is bundled local content — no network fetch needed, same
  // source AssessmentOptionsView.tsx reads from. Joined against the account's PLA list below so
  // only assessments actually enabled for this account are offered.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!adminAssessmentStorage) {
        return;
      }
      const list = await adminAssessmentStorage.getAvailableAssessments();
      if (!cancelled) {
        setAssessments(list);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [adminAssessmentStorage]);

  // Re-hydrate the draft form fields whenever the selected report changes (including reset to
  // a blank draft when selectedReportId is ''). Depends on reportDefs (not a ref) so a
  // just-applied initialReportId still hydrates once its def actually arrives from the
  // cache/network load above, even if that load was still in flight when selectedReportId was
  // first set — but hydratedForReportId guards against re-running for a report already
  // hydrated, so a later background refresh landing mid-edit doesn't stomp in-progress changes.
  useEffect(() => {
    if (!selectedReportId) {
      if (hydratedForReportId.current !== '') {
        hydratedForReportId.current = '';
        setDraftDName('');
        setDraftFilters([]);
        setDraftItems([]);
      }
      return;
    }
    if (hydratedForReportId.current === selectedReportId) {
      return;
    }
    const def = reportDefs.find((d) => d.ReportId === selectedReportId);
    if (def) {
      hydratedForReportId.current = selectedReportId;
      setDraftDName(def.DName);
      setDraftFilters(normalizeFilters(def.Filters));
      setDraftItems([...def.Items].sort(byPos));
    }
  }, [selectedReportId, reportDefs]);

  // Disarm the Remove confirmation whenever the selected report changes.
  useEffect(() => {
    setConfirmRemove(false);
  }, [selectedReportId]);

  const assessmentOptions = useMemo<AggAssessmentOption[]>(() => {
    const seen = new Set<string>();
    const result: AggAssessmentOption[] = [];
    plaList.forEach((pla) => {
      const assessmentId = (pla.AssessmentId || '').trim();
      if (!assessmentId || seen.has(assessmentId)) {
        return;
      }
      // Enabled = configured for this account and neither suspended nor deleted.
      if (pla.Suspended === '1' || pla.Status === '-1') {
        return;
      }
      seen.add(assessmentId);
      const content = assessments.find((a) => a.sourceId === assessmentId);
      result.push({
        value: assessmentId,
        label: content?.dName || pla.DName || assessmentId,
        aggVars: (content?.aggVars ?? []).map((v) => ({ value: v.mVar, label: v.dName || v.mVar })),
      });
    });
    return result;
  }, [plaList, assessments]);

  const persistDefsToCache = async (nextDefs: StatsAggReportDefDto[]) => {
    if (!storage || !accountId) {
      return;
    }
    try {
      const pracStorage = new AccountPracInfoStorage(storage);
      const cached = await pracStorage.getAccountPracInfo(accountId);
      if (cached?.AccountSettings) {
        await pracStorage.saveAccountPracInfo(accountId, {
          ...cached,
          AccountSettings: { ...cached.AccountSettings, AggReportDefs: nextDefs },
        });
      }
    } catch (cacheError) {
      console.warn('AggReportSetupView: failed to persist AggReportDefs cache', cacheError);
    }
  };

  const handleAddNewReport = () => {
    setSelectedReportId('');
    setSaveMessage(null);
    setError(null);
    setConfirmRemove(false);
  };

  const handleRemovePress = () => {
    if (!selectedReportId || isRemoving) {
      return;
    }
    // First press arms the confirmation; second press within the same armed state removes.
    if (!confirmRemove) {
      setConfirmRemove(true);
      return;
    }
    void handleRemoveReport();
  };

  const handleRemoveReport = async () => {
    if (!selectedReportId || !accountId || !aggReportService) {
      return;
    }
    const existing = reportDefs.find((d) => d.ReportId === selectedReportId);
    if (!existing) {
      return;
    }

    setIsRemoving(true);
    setError(null);
    try {
      await aggReportService.putDef(
        { AccountId: accountId, AggReportDef: { ...existing, Status: '-1' } },
        { userStore: resolveUserStore(), storage }
      );
      const nextDefs = reportDefs.filter((d) => d.ReportId !== selectedReportId);
      setReportDefs(nextDefs);
      await persistDefsToCache(nextDefs);
      setSelectedReportId('');
      setSaveMessage('Report removed.');
    } catch (removeError) {
      const message = removeError instanceof Error ? removeError.message : String(removeError);
      setError(`Remove failed: ${message}`);
    } finally {
      setIsRemoving(false);
      setConfirmRemove(false);
    }
  };

  const toggleFilter = (name: string) => {
    setDraftFilters((prev) =>
      prev.some((f) => f.Name === name)
        ? prev.filter((f) => f.Name !== name)
        : [...prev, { FilterId: '', Name: name, Pos: '' }]
    );
  };

  const handleSave = async () => {
    setConfirmRemove(false);
    if (!accountId) {
      setError('No account selected.');
      return;
    }
    if (!aggReportService) {
      setError('Aggregate report service unavailable.');
      return;
    }
    if (!draftDName.trim()) {
      setError('Report name is required.');
      return;
    }

    setIsSavingRemote(true);
    setError(null);
    setSaveMessage(null);

    try {
      const isNew = !selectedReportId;
      const payloadDef: StatsAggReportDefDto = {
        ReportId: isNew ? '' : selectedReportId,
        DName: draftDName.trim(),
        // Pos recomputed from array order at save time — see StatsAggFilterDefDto/…ItemDefDto.
        Filters: draftFilters.map((filter, index) => ({ ...filter, Pos: String(index + 1) })),
        Items: draftItems.map((item, index) => ({ ...item, Pos: String(index + 1) })),
        // '2' = create, '1' = update/active — see StatsAggReportDefDto.Status.
        Status: isNew ? '2' : '1',
      };

      const response = await aggReportService.putDef(
        { AccountId: accountId, AggReportDef: payloadDef },
        { userStore: resolveUserStore(), storage }
      );

      // The put response echoes the persisted definition with backend-assigned ids populated
      // (ReportId / each Item.ItemId / each Filter.FilterId) — adopt it wholesale so a
      // subsequent Save on this same still-open form sends the real ids for update processing.
      const persisted = response?.AggReportDef ?? payloadDef;
      const persistedReportId = persisted.ReportId || selectedReportId;

      const nextDefs = isNew
        ? [...reportDefs, persisted]
        : reportDefs.map((d) => (d.ReportId === selectedReportId ? persisted : d));

      setReportDefs(nextDefs);
      await persistDefsToCache(nextDefs);

      // Stay on the setup form (no navigation) — re-sync it to the persisted definition so
      // further editing continues from backend truth.
      hydratedForReportId.current = persistedReportId;
      setSelectedReportId(persistedReportId);
      setDraftDName(persisted.DName);
      setDraftFilters(normalizeFilters(persisted.Filters));
      setDraftItems([...persisted.Items].sort(byPos));
      setSaveMessage('Report saved.');
      onSaved(persistedReportId);
    } catch (saveError) {
      const message = saveError instanceof Error ? saveError.message : String(saveError);
      setError(`Save failed: ${message}`);
    } finally {
      setIsSavingRemote(false);
    }
  };

  const isWeb = Platform.OS === 'web';
  const webSelectStyle = { ...webControlStyle, minWidth: 200 } as any;

  const cycleValue = (options: string[], current: string, onChange: (value: string) => void) => {
    if (options.length === 0) {
      return;
    }
    const currentIndex = options.indexOf(current);
    const nextIndex = currentIndex < 0 || currentIndex === options.length - 1 ? 0 : currentIndex + 1;
    onChange(options[nextIndex]);
  };

  return (
    <ScrollView style={styles.panel} contentContainerStyle={styles.panelContent}>
      <View style={styles.contentInner}>
        <View style={styles.headerRow}>
          <Pressable onPress={onBack} style={styles.backInlineLink} accessibilityRole="button" accessibilityLabel="Back">
            <Text style={styles.backInlineLinkText}>← Back</Text>
          </Pressable>
        </View>

        {error ? (
          <View style={styles.errorCard}>
            <Text style={styles.errorText}>{error}</Text>
          </View>
        ) : null}
        {saveMessage ? (
          <View style={styles.saveMessageRow}>
            <Text style={styles.saveMessageText}>{saveMessage}</Text>
          </View>
        ) : null}

        <View style={styles.formTable}>
          <View style={styles.formRow}>
            <View style={styles.formLabelCell}>
              <Text style={styles.formLabelText}>Report</Text>
            </View>
            <View style={styles.formContentCell}>
              <View style={styles.inlineRow}>
                {isWeb ? (
                  <select
                    style={webSelectStyle}
                    value={selectedReportId}
                    onChange={(event: any) => setSelectedReportId(String(event?.target?.value ?? ''))}
                    aria-label="Report"
                  >
                    {(reportDefs.length === 0 || !selectedReportId) ? <option value="">New report…</option> : null}
                    {reportDefs.map((def) => (
                      <option key={def.ReportId} value={def.ReportId}>{def.DName || def.ReportId}</option>
                    ))}
                  </select>
                ) : (
                  <Pressable
                    style={styles.dropdownBtn}
                    onPress={() => cycleValue(reportDefs.map((d) => d.ReportId), selectedReportId, setSelectedReportId)}
                    accessibilityRole="button"
                    accessibilityLabel="Report"
                  >
                    <Text style={styles.dropdownBtnText}>
                      {reportDefs.find((d) => d.ReportId === selectedReportId)?.DName || 'New report…'}
                    </Text>
                    <Text style={styles.dropdownBtnChevron}>▼</Text>
                  </Pressable>
                )}

                <Pressable style={styles.smallButton} onPress={handleAddNewReport} accessibilityRole="button" accessibilityLabel="Add report">
                  <Text style={styles.smallButtonText}>Add</Text>
                </Pressable>

                <Pressable
                  style={[
                    styles.smallButton,
                    confirmRemove && styles.smallButtonDanger,
                    (!selectedReportId || isRemoving) && styles.smallButtonDisabled,
                  ]}
                  onPress={handleRemovePress}
                  disabled={!selectedReportId || isRemoving}
                  accessibilityRole="button"
                  accessibilityLabel={confirmRemove ? 'Confirm remove report' : 'Remove report'}
                >
                  <Text style={[styles.smallButtonText, confirmRemove && styles.smallButtonDangerText]}>
                    {isRemoving ? 'Removing…' : confirmRemove ? 'Confirm?' : 'Remove'}
                  </Text>
                </Pressable>

                {confirmRemove && !isRemoving ? (
                  <Pressable
                    style={styles.smallButton}
                    onPress={() => setConfirmRemove(false)}
                    accessibilityRole="button"
                    accessibilityLabel="Cancel remove report"
                  >
                    <Text style={styles.smallButtonText}>Cancel</Text>
                  </Pressable>
                ) : null}
              </View>
              {!selectedReportId ? <Text style={styles.newDraftHint}>New, unsaved report</Text> : null}
            </View>
          </View>

          <View style={styles.formRow}>
            <View style={styles.formLabelCell}>
              <Text style={styles.formLabelText}>Name</Text>
            </View>
            <View style={styles.formContentCell}>
              <TextInput
                style={styles.textInput}
                value={draftDName}
                onChangeText={setDraftDName}
                placeholder="Report name"
                placeholderTextColor={EDITOR_COLORS.placeholder}
              />
            </View>
          </View>

          <View style={styles.formRow}>
            <View style={styles.formLabelCell}>
              <Text style={styles.formLabelText}>Filters</Text>
            </View>
            <View style={styles.formContentCell}>
              {AGG_FILTER_CATALOG.map((entry) => {
                const isChecked = draftFilters.some((f) => f.Name === entry.name);
                if (isWeb) {
                  return (
                    <View key={entry.name} style={styles.checkboxRow}>
                      {renderWebOutlineCheckbox(isChecked, () => toggleFilter(entry.name), entry.dName)}
                      <Text style={styles.checkboxLabel}>{entry.dName}</Text>
                    </View>
                  );
                }
                return (
                  <Pressable
                    key={entry.name}
                    style={styles.checkboxRow}
                    onPress={() => toggleFilter(entry.name)}
                    accessibilityRole="checkbox"
                    accessibilityState={{ checked: isChecked }}
                  >
                    <OutlineCheckboxBox checked={isChecked} />
                    <Text style={styles.checkboxLabel}>{entry.dName}</Text>
                  </Pressable>
                );
              })}
            </View>
          </View>

          <View style={styles.formRow}>
            <View style={styles.formLabelCell}>
              <Text style={styles.formLabelText}>Report Items</Text>
            </View>
            <View style={styles.formContentCell}>
              <AggReportItemListEditor
                items={draftItems}
                onChange={setDraftItems}
                assessmentOptions={assessmentOptions}
              />
            </View>
          </View>
        </View>

        <View style={styles.saveRow}>
          <Pressable
            style={[styles.saveButton, isSavingRemote && styles.saveButtonDisabled]}
            onPress={() => { void handleSave(); }}
            disabled={isSavingRemote}
            accessibilityRole="button"
            accessibilityLabel="Save"
          >
            <Text style={styles.saveButtonText}>{isSavingRemote ? 'Saving…' : 'Save'}</Text>
          </Pressable>
        </View>
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  panel: {
    flex: 1,
    backgroundColor: EDITOR_COLORS.background,
  },
  panelContent: {
    padding: 20,
    paddingBottom: 40,
    alignItems: 'center',
  },
  contentInner: {
    width: '100%',
    maxWidth: 900,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 14,
  },
  backInlineLink: {
    marginRight: 10,
  },
  backInlineLinkText: {
    ...editorText,
    color: EDITOR_COLORS.accent,
    textDecorationLine: 'none',
  },
  errorCard: {
    backgroundColor: EDITOR_COLORS.errorBackground,
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
  },
  errorText: {
    ...editorText,
    color: EDITOR_COLORS.errorText,
    lineHeight: 20,
  },
  saveMessageRow: {
    paddingBottom: 12,
  },
  saveMessageText: {
    ...editorText,
    color: EDITOR_COLORS.successText,
  },
  formTable: {
    flexDirection: 'column',
  },
  formRow: {
    flexDirection: 'row',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: EDITOR_COLORS.separator,
  },
  formLabelCell: {
    width: 110,
    paddingTop: 4,
  },
  formLabelText: {
    ...editorText,
    color: EDITOR_COLORS.textSecondary,
  },
  formContentCell: {
    flex: 1,
  },
  inlineRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  newDraftHint: {
    ...editorText,
    color: EDITOR_COLORS.textMuted,
    fontStyle: 'italic',
    marginTop: 4,
  },
  textInput: {
    ...editorText,
    height: 28,
    maxWidth: 320,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.controlBorder,
    borderRadius: 3,
    paddingHorizontal: 7,
    color: EDITOR_COLORS.textPrimary,
    backgroundColor: EDITOR_COLORS.background,
  },
  dropdownBtn: {
    height: 28,
    minWidth: 200,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.controlBorder,
    borderRadius: 3,
    paddingHorizontal: 7,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: EDITOR_COLORS.background,
  },
  dropdownBtnText: {
    ...editorText,
    color: EDITOR_COLORS.textPrimary,
    flex: 1,
  },
  dropdownBtnChevron: {
    ...editorText,
    fontSize: 11,
    color: EDITOR_COLORS.textMuted,
    marginLeft: 4,
  },
  smallButton: {
    height: 28,
    paddingHorizontal: 12,
    borderRadius: 5,
    backgroundColor: EDITOR_COLORS.submitBackground,
    alignItems: 'center',
    justifyContent: 'center',
  },
  smallButtonDisabled: {
    backgroundColor: EDITOR_COLORS.submitBackgroundDisabled,
  },
  smallButtonDanger: {
    backgroundColor: EDITOR_COLORS.errorBackground,
  },
  smallButtonText: {
    ...editorText,
    color: EDITOR_COLORS.submitText,
  },
  smallButtonDangerText: {
    color: EDITOR_COLORS.errorText,
    fontWeight: '700',
  },
  checkboxRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 3,
    gap: 8,
  },
  checkboxLabel: {
    ...editorText,
    color: EDITOR_COLORS.textSecondary,
  },
  saveRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: 16,
  },
  saveButton: {
    width: 120,
    height: 28,
    borderRadius: 5,
    backgroundColor: EDITOR_COLORS.submitBackground,
    alignItems: 'center',
    justifyContent: 'center',
  },
  saveButtonDisabled: {
    backgroundColor: EDITOR_COLORS.submitBackgroundDisabled,
  },
  saveButtonText: {
    ...editorText,
    color: EDITOR_COLORS.submitText,
  },
});
